import {
  Button,
  DropdownWithButton,
  DropdownListItem,
  Pill,
  Table,
  useSortableTable,
  type ButtonComponentProps,
  type TableColumn
} from '@hm/fundamental'
import {mdiDotsVertical} from '@mdi/js'
import {forwardRef, useMemo} from 'react'

import {formatDate, statusColor, type SortDirection} from '../../helpers'
import {useRiskSurveyTranslation} from '../../hooks'
import {RiaStatus, type ReportSummaryDto} from '../../types'

type ReportsTableProps = {
  reports: ReportSummaryDto[]
  showCountry: boolean
  onSortChange: (sortBy: string, sortDir: SortDirection) => void
  currentUserId?: string
  onEditSite: (report: ReportSummaryDto) => void
  onArchive: (reportId: string) => void
  onUnarchive: (reportId: string) => void
  onExport: (reportId: string, siteName: string) => void
  onRowClick: (report: ReportSummaryDto) => void
}

export const ReportsTable = ({
  reports,
  showCountry,
  onSortChange,
  currentUserId,
  onEditSite,
  onArchive,
  onUnarchive,
  onExport,
  onRowClick
}: ReportsTableProps) => {
  const {t, i18n} = useRiskSurveyTranslation()

  const ActionMenuTrigger = useMemo(
    () =>
      forwardRef<HTMLButtonElement, ButtonComponentProps>(({onClick, isDropdownOpen}, ref) => (
        <Button
          ref={ref}
          variant="tertiary"
          icon={mdiDotsVertical}
          square
          toggled={isDropdownOpen}
          onClick={onClick}
        />
      )),
    []
  )

  const columns: TableColumn<ReportSummaryDto>[] = useMemo(
    () => [
      ...(showCountry
        ? ([
            {
              id: 'country',
              label: t('table.country'),
              sortable: true,
              render: (row) => row.country ?? '—'
            }
          ] as TableColumn<ReportSummaryDto>[])
        : []),
      {id: 'siteName', label: t('table.siteName'), sortable: true},
      {
        id: 'uploadedAt',
        label: t('table.uploadedAt'),
        sortable: true,
        render: (row) => formatDate(row.uploadedAt, i18n.language)
      },
      {id: 'riaCount', label: t('table.riaCount'), sortable: true},
      {
        label: t('table.openCount'),
        render: (row) => {
          const open =
            (row.statusBreakdown[RiaStatus.New] ?? 0) +
            (row.statusBreakdown[RiaStatus.Pending] ?? 0) +
            (row.statusBreakdown[RiaStatus.InProgress] ?? 0)
          return open
        }
      },
      {
        label: t('table.status'),
        render: (row) => {
          const entries = Object.entries(row.statusBreakdown).filter(([, count]) => count > 0)
          return (
            <div className="flex gap-1 flex-wrap">
              {row.isArchived && (
                <Pill
                  type="neutral"
                  text={t('archive.archivedBadge')}
                  size="regular"
                  data-cy="archived-badge"
                />
              )}
              {entries.map(([status, count]) => (
                <Pill
                  key={status}
                  type={statusColor[status as RiaStatus] ?? 'neutral'}
                  text={`${t(`status.${status}`)}: ${count}`}
                  size="regular"
                  data-cy={`status-summary-${status}`}
                />
              ))}
            </div>
          )
        }
      },
      {
        label: '',
        render: (row) => {
          const isCreator = !!currentUserId && row.uploadedByUserId === currentUserId

          return (
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownWithButton
                variant="tertiary"
                component={ActionMenuTrigger}
                data-cy={`report-action-menu-${row.id}`}
              >
                <DropdownListItem
                  title={t('plant.export')}
                  onClick={() => onExport(row.id, row.siteName ?? '')}
                  data-cy="action-export"
                />
                {isCreator && !row.isArchived && (
                  <DropdownListItem
                    title={t('editSite.action')}
                    onClick={() => onEditSite(row)}
                    data-cy="action-edit-site"
                  />
                )}
                {isCreator && !row.isArchived && (
                  <DropdownListItem
                    title={t('archive.archive')}
                    onClick={() => onArchive(row.id)}
                    data-cy="action-archive"
                  />
                )}
                {isCreator && row.isArchived && (
                  <DropdownListItem
                    title={t('archive.unarchive')}
                    onClick={() => onUnarchive(row.id)}
                    data-cy="action-unarchive"
                  />
                )}
              </DropdownWithButton>
            </div>
          )
        }
      }
    ],
    [
      ActionMenuTrigger,
      currentUserId,
      i18n.language,
      onArchive,
      onEditSite,
      onExport,
      onUnarchive,
      showCountry,
      t
    ]
  )

  const config = useSortableTable<ReportSummaryDto>(
    columns,
    [],
    {
      prefix: 'report-row',
      postfixFieldName: 'id'
    },
    undefined,
    (sortKey, direction) => onSortChange(sortKey, direction as SortDirection)
  )

  return (
    <div className="[&_thead_th]:text-base [&_thead_th]:font-semibold [&_thead_th]:leading-normal [&_thead_th_*]:text-base [&_thead_th_*]:font-semibold [&_thead_th_*]:leading-normal [&_tr[data-row-id^=report-row-archived]]:opacity-60">
      <Table
        config={config}
        data={reports}
        getRowId={(row) => (row.isArchived ? `archived-${row.id}` : row.id)}
        breakpoint="sm"
        onRowClick={onRowClick}
        data-cy="reports-table"
      />
    </div>
  )
}
